import moment from 'moment';
import { memoize } from '@philips-internal/oswald-ui-utils';
import { DatePickerMode } from './date-picker-constants.js';
const CONSTANTS = {
    DAY: 'day',
    HOUR: 'hour',
    MINUTE: 'minute',
    MONTH: 'month',
    WEEK: 'week',
    YEAR: 'year',
};
/**
 * Creates a grid limit object with the week and month start and end.
 *
 * @param date - The date to use as a base for the start and end of the limits.
 */
function getGridLimits(date) {
    const monthStart = moment.utc(date).startOf(CONSTANTS.MONTH);
    const monthEnd = moment.utc(date).endOf(CONSTANTS.MONTH);
    const gridStart = moment.utc(monthStart).startOf(CONSTANTS.WEEK);
    const gridEnd = moment.utc(monthEnd).endOf(CONSTANTS.WEEK);
    const gridLimits = {
        gridEnd,
        gridStart,
        monthEnd,
        monthStart,
    };
    return gridEnd.diff(gridStart, CONSTANTS.WEEK) < 5
        ? { ...gridLimits, gridEnd: gridEnd.add(1, CONSTANTS.WEEK) }
        : gridLimits;
}
/**
 * Obtains the weekday names.
 *
 * @param short - If the names should be the short version.
 */
function getWeekDays(short = false) {
    return short ?
        moment.weekdaysShort(true) :
        moment.weekdays(true);
}
/**
 * Converts a range values to moment dates.
 *
 * @param startDate - The starting date.
 * @param endDate - The ending date.
 */
function toMoment({ startDate, endDate }) {
    return {
        endDate: moment.utc(endDate),
        startDate: moment.utc(startDate),
    };
}
/**
 * Compares two ranges by their start dates.
 *
 * @param aRange - The first range to compare.
 * @param bRange - The second range to compare.
 */
function byInitialDate(aRange, bRange) {
    if (aRange.startDate.isSame(bRange.startDate)) {
        return 0;
    }
    return aRange.startDate.isBefore(bRange.startDate) ? -1 : 1;
}
/**
 * Sorts the ranges by their start dates and optimizes the result.
 */
const optimizeRanges = memoize((ogRanges) => {
    if (!ogRanges.length) {
        return [];
    }
    const [initRange, ...ranges] = ogRanges.map(toMoment).sort(byInitialDate);
    const optimizedRanges = [initRange];
    for (const range of ranges) {
        const { startDate: currentStartDate, endDate: currentEndDate } = range;
        const lastRange = optimizedRanges[optimizedRanges.length - 1];
        const { endDate: lastEndDate } = lastRange;
        if (currentEndDate.isSameOrBefore(lastEndDate)) {
            continue;
        }
        else if (currentStartDate.isSameOrBefore(lastEndDate)) {
            lastRange.endDate = currentEndDate;
        }
        else {
            optimizedRanges.push(range);
        }
    }
    return optimizedRanges;
});
/**
 * Creates a 3x4 grid with the previous years starting from the year of the given date.
 *
 * @param options - The grid options.
 */
function getYearGrid(options) {
    const { date } = options;
    const copy = moment.utc(date);
    const years = [];
    const year = {
        active: true,
        date: moment.utc(copy),
    };
    years.push(year);
    for (let index = 0; index <= 10; index++) {
        copy.add(-1, CONSTANTS.YEAR);
        const newYear = {
            active: true,
            date: moment.utc(copy),
        };
        years.unshift(newYear);
    }
    const yearGrid = {
        array: years,
        numOfRows: 3,
        sizeOfRows: 4,
    };
    return transformToGrid(yearGrid);
}
/**
 * Creates a 3x4 grid with all the months of the year of the given date.
 *
 * @param options - The grid options.
 */
function getAllMonthsGrid(options) {
    const { date } = options;
    const firstDayOfNextYear = moment.utc(date).endOf(CONSTANTS.YEAR).add(1, CONSTANTS.DAY);
    const copy = moment.utc(firstDayOfNextYear);
    const months = [];
    for (let index = 0; index <= 11; index++) {
        copy.add(-1, CONSTANTS.MONTH);
        const month = {
            active: true,
            date: moment.utc(copy),
        };
        months.unshift(month);
    }
    const montGrid = {
        array: months,
        numOfRows: 3,
        sizeOfRows: 4,
    };
    return transformToGrid(montGrid);
}
/**
 * Obtains the moment granularity for the date picker mode.
 *
 * @param mode - The date picker mode.
 */
function getGranularity(mode) {
    switch (mode) {
        case DatePickerMode.WEEK:
        case DatePickerMode.MONTHLY:
            return 'date';
        case DatePickerMode.YEARLY:
            return 'month';
        case DatePickerMode.DECADES:
            return 'year';
    }
}
/**
 * Obtains the next bigger mode for the date picker mode.
 *
 * @param mode - The date picker mode.
 */
function incrementMode(mode) {
    switch (mode) {
        case DatePickerMode.DECADES:
            return DatePickerMode.MONTHLY;
        case DatePickerMode.MONTHLY:
            return DatePickerMode.YEARLY;
        default:
            return DatePickerMode.DECADES;
    }
}
/**
 * Obtains the next smaller mode for the date picker mode.
 *
 * @param mode - The date picker mode.
 */
function decrementMode(mode) {
    switch (mode) {
        case DatePickerMode.DECADES:
            return DatePickerMode.YEARLY;
        default:
            return DatePickerMode.MONTHLY;
    }
}
/**
 * Transforms an array in a grid with the specified number of rows. The product of numOfRows * sizeOfRows needs to be a pair number.
 *
 * @param options - The array to transform.
 */
function transformToGrid(options) {
    const { array, numOfRows, sizeOfRows } = options;
    const part = [];
    if (array.length % numOfRows === 0) {
        let numOfPartsCount = 0;
        while (part.length !== numOfRows) {
            part.push(array.slice(numOfPartsCount, numOfPartsCount + sizeOfRows));
            numOfPartsCount += sizeOfRows;
        }
    }
    return part;
}
/**
 * Creates a grid with the days of the month of the current date and the first seven days of the next month.
 *
 * @param options - The grid options.
 */
function getMonthGridInterval(options) {
    const { date, endIntervalDate, events = [], startIntervalDate } = options;
    const startOfMonth = moment.utc(date).startOf(CONSTANTS.MONTH);
    const nextMonth = moment
        .utc(startOfMonth)
        .add(1, CONSTANTS.MONTH)
        .startOf(CONSTANTS.DAY)
        .startOf(CONSTANTS.MONTH);
    const lastDaysOfMonth = getLastDaysOfMonth(startOfMonth, events, [], startIntervalDate, endIntervalDate);
    const daysOfMonth = getDaysOfMonth(startOfMonth, date, events, lastDaysOfMonth, startIntervalDate, endIntervalDate);
    return addNextSevenDays(nextMonth, events, daysOfMonth);
}
/**
 * Adds the next seven days, starting at the given {@link date}, to the {@link weeks} grid.
 *
 * @param date - The base date.
 * @param events - The days with events.
 * @param weeks - The initial grid data.
 * @param startIntervalDate - The start date of the interval.
 * @param endIntervalDate - The end date of the interval.
 */
function addNextSevenDays(date, events, weeks, startIntervalDate, endIntervalDate) {
    const lastWeek = weeks[weeks.length - 1];
    const copy = moment.utc(date);
    for (let index = lastWeek.length; index < 7; index++) {
        lastWeek.push(getDay({
            date: copy,
            endIntervalDate,
            events,
            startIntervalDate,
        }));
        copy.add(1, CONSTANTS.DAY);
    }
    return weeks;
}
/**
 * Creates a day object checking if a event or special day with the same date exists.
 *
 * @param options - The grid options.
 */
function getDay(options) {
    const { date, endIntervalDate, events = [], specialDays = [], startIntervalDate, } = options;
    const day = events.find((event) => date.isSame(event.date, 'date'));
    const specialDay = specialDays.find((specialDay) => date.isSame(moment.utc(specialDay.date), 'date'));
    let isDateInInterval = false;
    if (startIntervalDate !== undefined && endIntervalDate !== undefined) {
        isDateInInterval = moment.utc(date).isBetween(moment.utc(startIntervalDate), moment.utc(endIntervalDate));
    }
    if (day != null) {
        return {
            ...day,
            date: moment.utc(day.date),
            end: day.end != null ? moment.utc(day.end) : null,
            events: day.events?.map(item => ({
                ...item,
                date: moment.utc(item.date),
                end: moment.utc(item.end),
            })) ?? [],
            isDateInInterval,
            specialDay,
        };
    }
    return {
        active: true,
        busyPercent: 0,
        date: moment.utc(date),
        events: [],
        isDateInInterval,
        specialDay,
    };
}
/**
 * Obtains the days of the {@link month} starting from the given {@link date}.
 *
 * @param date - The base date.
 * @param month - The month.
 * @param events - The days with events.
 * @param weeks - The initial grid data.
 * @param startIntervalDate - The start date of the interval.
 * @param endIntervalDate - The end date of the interval.
 */
function getDaysOfMonth(date, month, events, weeks, startIntervalDate, endIntervalDate) {
    let weekIndex = 0;
    const copy = moment.utc(date).startOf(CONSTANTS.DAY);
    while (copy.isSame(month, CONSTANTS.MONTH)) {
        if (weeks[weekIndex].length >= 7) {
            weeks.push([]);
            weekIndex += 1;
        }
        weeks[weekIndex].push(getDay({
            date: copy,
            endIntervalDate,
            events,
            startIntervalDate,
        }));
        copy.add(1, CONSTANTS.DAY);
    }
    return weeks;
}
/**
 * Obtains the last days of the month of the given {@link date}.
 *
 * @param date - The base date.
 * @param events - The days with events.
 * @param weeks - The initial grid data.
 * @param startIntervalDate - The start date of the interval.
 * @param endIntervalDate - The end date of the interval.
 */
function getLastDaysOfMonth(date, events, weeks, startIntervalDate, endIntervalDate) {
    const difference = date.day() === 0
        ? (7 - moment.localeData().firstDayOfWeek())
        : date.day() - moment.localeData().firstDayOfWeek();
    const copy = moment.utc(date);
    weeks[0] = [];
    for (let index = 0; index < difference; index++) {
        copy.add(-1, CONSTANTS.DAY);
        weeks[0].unshift(getDay({
            date: copy,
            endIntervalDate,
            events,
            startIntervalDate,
        }));
    }
    return weeks;
}
/**
 * Obtains the corresponding title format for the calendar for the given {@link mode}.
 *
 * @param mode - The date picker mode.
 * @param date - The date.
 */
function getCalendarTitleFormat(mode, date) {
    switch (mode) {
        case DatePickerMode.WEEK:
            return date.format('DD');
        case DatePickerMode.MONTHLY:
            return date.format('DD');
        case DatePickerMode.YEARLY:
            return date.format('MMM');
        case DatePickerMode.DECADES:
            return date.format('YYYY');
    }
}
/**
 * Obtains the corresponding navigability mode for the calendar for the given {@link mode}.
 *
 * @param mode - The date picker mode.
 */
function getTypeOfNavigabilityMode(mode) {
    switch (mode) {
        case DatePickerMode.MONTHLY:
            return 'month';
        case DatePickerMode.WEEK:
            return 'month';
        default:
            return 'year';
    }
}
/**
 * Obtains a dates grid accondingly to the date picker mode.
 *
 * @param options - The grid options.
 */
function getDataGrid(options) {
    const { isIntervalMode = false, mode } = options;
    if (isIntervalMode) {
        return getMonthGridInterval(options);
    }
    switch (mode) {
        case DatePickerMode.DECADES:
            return getYearGrid(options);
        case DatePickerMode.MONTHLY:
            return getMonthGrid(options);
        case DatePickerMode.WEEK:
            return getMonthGrid(options);
        case DatePickerMode.YEARLY:
        default:
            return getAllMonthsGrid(options);
    }
}
/**
 * Checks if the date is selected.
 *
 * @param isSelected - If the date is selected.
 * @param active - If the date is active.
 * @param mode - The date picker mode.
 */
function isSelectedAndActive(isSelected, active, mode) {
    return isSelected && active || isSelected && mode !== DatePickerMode.MONTHLY;
}
/**
 * Checks if the date is visible.
 *
 * @param isIntervalMode - If the date picker is an interval picker.
 * @param active - If the date is active.
 */
function isVisible(isIntervalMode, active) {
    return isIntervalMode && !active;
}
/**
 * Checks if the {@link date} is the current date and it is not an interval picker.
 *
 * @param isIntervalMode - If the date picker is an interval picker.
 * @param date - The date to check.
 */
function isCurrent(isIntervalMode, date) {
    return !isIntervalMode && moment().utc().isSame(date, 'date');
}
/**
 * Checks if the {@link events} has values and it is not an interval picker.
 *
 * @param isIntervalMode - If the date picker is an interval picker.
 * @param events - The days with events.
 */
function hasEvents(isIntervalMode, events) {
    return !isIntervalMode && Boolean(events?.length);
}
/**
 * Checks if the date is selected.
 *
 * @param isIntervalMode - If the date picker is an interval picker.
 * @param isDateInInterval - If the date is in the interval.
 * @param startIntervalDate - The start date of the interval.
 * @param date - The date.
 * @param endIntervalDate - The end date of the interval.
 * @param currentValue - The current date picker value.
 * @param mode - The date picker mode.
 */
function isSelected(isIntervalMode, isDateInInterval, startIntervalDate, date, endIntervalDate, currentValue, mode) {
    return isIntervalMode
        ? (isDateInInterval
            || Boolean(startIntervalDate) && moment(startIntervalDate).utc().isSame(date.utc(), 'date')
            || Boolean(endIntervalDate) && moment(endIntervalDate).utc().isSame(date.utc(), 'date'))
        : moment(date).isSame(currentValue, getGranularity(mode));
}
/**
 * Checks if both limits are true.
 *
 * @param isFirstLimit - The first limit.
 * @param isLastLimit - The last limit.
 */
function isLimit(isFirstLimit, isLastLimit) {
    return isFirstLimit === true || isLastLimit === true;
}
function isIntervalLimit(intervalDate, date) {
    return intervalDate ? intervalDate.isSame(date, 'date') : false;
}
function getMonthGrid({ date, events, specialDays }) {
    const startOfMonth = moment.utc(date).startOf(CONSTANTS.MONTH);
    const gridLimits = getGridLimits(startOfMonth);
    return buildMonthGridFromLimits(gridLimits, events, specialDays);
}
/**
 * Creates a grid with the days between the grid limits.
 *
 * @param gridLimits - The grid limits.
 * @param events - The days with events.
 * @param specialDays - The special days.
 */
function buildMonthGridFromLimits(gridLimits, events = [], specialDays = []) {
    const gridDays = gridLimits.gridEnd.diff(gridLimits.gridStart, CONSTANTS.DAY);
    const date = moment.parseZone(gridLimits.gridStart).startOf(CONSTANTS.DAY);
    const weeks = gridDays / 7;
    const gridList = [];
    for (let weekCount = 0; weekCount < weeks; weekCount++) {
        const weekList = [];
        for (let count = 0; count < 7; count++) {
            const day = getDay({ date, events, specialDays });
            weekList.push(day);
            date.add(1, CONSTANTS.DAY);
        }
        gridList.push(weekList);
    }
    return gridList;
}
/**
 * Obtains a range of dates for the week of the given {@link initialDate}.
 *
 * @param initialDate - The initial date.
 */
function getWeekDaysRange(initialDate) {
    const firstWeeksDay = moment(initialDate).startOf(CONSTANTS.WEEK);
    return moment.weekdays().map((_, index) => firstWeeksDay.clone().add(index, 'days'));
}
export { getGridLimits, getWeekDays, getYearGrid, getAllMonthsGrid, getGranularity, incrementMode, decrementMode, getMonthGridInterval, getCalendarTitleFormat, getTypeOfNavigabilityMode, getDataGrid, isSelectedAndActive, isVisible, isCurrent, hasEvents, isSelected, isLimit, isIntervalLimit, getMonthGrid, getWeekDaysRange, optimizeRanges, };
//# sourceMappingURL=date-picker-util.js.map